/**
 * Generic layout-driven ordering helper (Chantier A, 2026-08-21).
 *
 * Reorders a list of items to mirror the order of their field ids inside a
 * Rider Layout section.
 *
 * ⚠️ Items whose id is ABSENT from the layout are inserted at their DEFAULT
 * position (i.e. where they sit in `items`, which the config pages author in the
 * default order) — NOT appended at the end. Appending made every newly added
 * field show up last on its config page until the layout was saved again
 * (bug v0.584: the new `sound.monitor-console` block appeared after
 * `sound.limit` because the band's saved sound field list predated it, while the
 * PDF and the layout editor already placed it correctly). Same semantics as
 * `insertFieldsAtDefaultPositions` in `riderLayout.ts`.
 */
export function sortByLayoutOrder<T>(
  items: T[],
  layoutOrder: string[] | null,
  getId: (item: T) => string,
): T[] {
  if (!layoutOrder || layoutOrder.length === 0) return items;
  const defaultIds = items.map(getId);
  // Un id périmé (présent dans le layout, plus dans la page) ne doit pas
  // consommer un rang : on ne garde que les ids réellement affichés.
  const order = layoutOrder.filter((id) => defaultIds.includes(id));
  for (const id of defaultIds) {
    if (order.includes(id)) continue;
    // Position par défaut : juste avant le premier id SUIVANT déjà présent.
    const di = defaultIds.indexOf(id);
    let insertAt = order.length;
    for (let k = di + 1; k < defaultIds.length; k++) {
      const at = order.indexOf(defaultIds[k]);
      if (at >= 0) {
        insertAt = at;
        break;
      }
    }
    order.splice(insertAt, 0, id);
  }
  const rank = new Map(order.map((id, i) => [id, i]));
  return [...items].sort((a, b) => (rank.get(getId(a)) ?? 0) - (rank.get(getId(b)) ?? 0));
}
